<template>
  <div class="opportunity-analysis">
    <OpportunityHeader />
    <BaseLoadingBar v-if="opportunityPending" />

    <div class="opportunity-analysis__content">
      <div
        ref="menuContainer"
        class="opportunity-analysis__content__contextmenu-container"
        v-show="menuContainer.show"
        :style="{
          left: menuContainer.left + 'px',
          top: menuContainer.top + 'px',
        }"
      >
        <ContextualMenu
          ref="mainMenu"
          v-show="mainMenuShowed"
          :document-id="documentId"
          :opportunity-id="opportunityId"
          :tagActive="tagModalShowed"
          :taskModalActive="taskModalShowed"
          :colorModalActive="colorModalShowed"
          :annotation-to-highlight="annotationToHighlight"
          @show-tag-modal="showTagModal"
          @hide-tag-modal="hideTagModal"
          @show-task-modal="showTaskModal"
          @hide-task-modal="hideTaskModal"
          @show-color-modal="showColorModal"
          @hide-color-modal="hideColorModal"
          @set-color-menu-position="setColorMenuPosition"
          @highlight-annotation="highlightAnnotation"
          @click-outside="closeMenu"
        />
        <!-- Do not replace v-if with v-show as this would break the keywords search -->
        <EvaluationLinkMenu
          v-if="tagModalShowed"
          :selection="selectedAnnotation"
          :horizontal-position="getHorizontalPosition"
          :vertical-position="getVerticalPosition"
          @linkSelectionToItem="linkSelectionToItem"
          @linkSelectionToSelectItem="linkSelectionToSelectItem"
          @update="updateLinkSelectionToItem"
          @highlight-annotation="highlightAnnotation"
          @close="hideTagModal"
          @sizeChanged="checkViewportExceed"
          :opportunityId="opportunityId"
          searchAllowed
        />
        <!-- Do not replace v-if with v-show as this would break the keywords search -->
        <TaskLinkMenu
          v-if="taskModalShowed"
          :horizontal-position="getHorizontalPosition"
          :vertical-position="getVerticalPosition"
          @task-create="onTaskCreateMenuOptionClick"
          @link-selection-to-task="onLinkSelectionToTask"
          @highlight-annotation="highlightAnnotation"
          @close="hideTaskModal"
          @sizeChanged="checkViewportExceed"
          :opportunityId="opportunityId"
          searchAllowed
        />

        <ColorAnnotationMenu
          v-if="colorModalShowed"
          class="pdf-document-annotator__contextmenu-box"
          :colors="annotationColorPresets"
          :selected-color="activeAnnotationColor"
          :style="{
            top: `${colorMenuPosition.top}px`,
            left: `${colorMenuPosition.left}px`,
          }"
          @select-color="onSelectAnnotationColor"
        />
      </div>

      <PDFThumbnailSidebar
        v-if="pdf && !pendingRendering"
        v-show="showThumbnails && !isPhone"
        :pdf-document="pdf"
        :current-page="currentPageNumber"
        @page-click="scroll"
      />
      <div class="opportunity-analysis__center">
        <div class="opportunity-analysis__documents-header">
          <template v-if="!opportunityDocumentsPending">
            <div class="opportunity-analysis__header-left">
              <BaseRoundButton
                v-if="!isPhone"
                :icon="showThumbnails ? 'chevrons-left' : 'chevrons-right'"
                :size="28"
                is-outline
                :tooltip-text="
                  showThumbnails ? $t('page.analysis.thumbnails.hide') : $t('page.analysis.thumbnails.show')
                "
                tooltip-placement="bottom"
                @click.native="showThumbnails = !showThumbnails"
              />
              <div class="opportunity-analysis__documents--select">
                <BaseSelect
                  name="Queries"
                  documentIcon="file-text"
                  v-model="opportunityDocument"
                  :items="pdfDocuments"
                  document
                  item-text="name"
                  item-value="id"
                  @change="updateCurrentDocumentFromBaseSelect"
                  isSmall
                />
              </div>
            </div>
            <div class="opportunity-analysis__header-center">
              <PDFPaginator :document="pdf" @scrollToPage="scroll" />
              <div class="opportunity-analysis__header-separator" />
              <PDFZoom />
            </div>
            <div class="opportunity-analysis__header-right">
              <DocumentPermissions :document="opportunityDocument" />
              <BaseRoundButton
                icon="download"
                :size="28"
                is-transparent
                :disabled="documentDownloading"
                :tooltip-text="$t('actions.download')"
                tooltip-placement="bottom"
                @click.native="downloadDocument(opportunityDocument)"
              />
            </div>
          </template>
        </div>
        <div class="opportunity-analysis__pdf-content" ref="documentContent">
          <BaseCircularLoadingBar v-if="pendingRendering && !extractionSearchLoading" :size="32" />
          <template v-if="!pendingRendering">
            <div
              class="opportunity-analysis__pdf-content-viewer"
              @mouseup="(e) => onmouseup(e)"
              @mousedown="(e) => onmousedown(e)"
            >
              <PDFDocument ref="pdfDocument" @page-rendered="pageRendered" @scroll="closeMenu" />
            </div>
          </template>
          <!-- Phones and tablets: the selected keyword's occurrences, and the way back to the task. -->
          <KeywordOccurrenceBar v-if="isSinglePanel" class="opportunity-analysis__occurrences" />
          <button
            v-if="isSinglePanel && selectedTask && taskAside"
            type="button"
            class="opportunity-analysis__back-to-task"
            @click="taskAside = false"
          >
            <BaseIcon name="arrow-left" />
            {{ $t('page.analysis.back-to-task') }}
          </button>
          <v-overlay :value="extractionSearchLoading" absolute opacity="1" color="#ffffff" z-index="1">
            <BaseCircularLoadingBar :size="32" />
          </v-overlay>
        </div>
      </div>
      <div
        v-show="!isSinglePanel || panel !== 'document'"
        class="opportunity-analysis__panel--pdfNavigator"
        :class="{ 'is-over-document': isSinglePanel }"
      >
        <PDFNavigator
          :opportunityId="opportunityId"
          :tab="isSinglePanel ? navigatorTab : null"
          @task-click="onTaskEdit"
          @task-create="onTaskCreateClick"
          @task-assign="onTaskAssign"
          @tasks-import="onImportTasks"
        />
      </div>

      <transition name="analysis-task-dialog-slide" @after-enter="onTaskDialogTransitionEnd">
        <div
          v-if="selectedTask"
          v-show="!(isSinglePanel && taskAside)"
          class="opportunity-analysis__task-dialog"
          :class="{ 'opportunity-analysis__task-dialog--fullscreen': isTaskDialogFullScreen }"
        >
          <TaskDialog
            ref="taskDialog"
            :task="selectedTask"
            @close="onTaskDialogClose"
            @submit="onTaskEdit"
            @delete-citation="onTaskCitationDelete"
            @fullscreen="isTaskDialogFullScreen = $event"
            @scroll-to-page="onScrollToPage"
          />
        </div>
      </transition>
      <!-- Phones and tablets: the document or one of its panels, a tap apart. -->
      <BaseBottomTabs
        v-if="isSinglePanel"
        v-model="panel"
        :tabs="panelTabs"
        :label="$t('page.analysis.panels.label')"
        class="opportunity-analysis__panels"
      />
    </div>
    <v-overlay :value="opportunityDocumentsPending" absolute opacity="0.5" color="#ffffff" z-index="3">
      <BaseCircularLoadingBar is-small />
    </v-overlay>
  </div>
</template>

<script>
import BaseBottomTabs from '@/components/base/BaseBottomTabs.vue';
import BaseCircularLoadingBar from '@/components/base/BaseCircularLoadingBar';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseRoundButton from '@/components/base/BaseRoundButton.vue';
import BaseSelect from '@/components/base/BaseSelect';
import ContextualMenuMixin from '@/mixins/contextual-menu-mixin';
import ColorAnnotationMenu from '@/components/pdf-viewer/ColorAnnotationMenu.vue';
import ContextualMenu from '@/components/pdf-viewer/ContextualMenu.vue';
import OpportunityHeader from '@/components/page-opportunities/OpportunityHeader';
import PDFDocument from '@/components/pdf-viewer/PDFDocument.vue';
import PDFNavigator from '@/components/pdf-viewer/PDFNavigator.vue';
import PDFThumbnailSidebar from '@/components/pdf-viewer/PDFThumbnailSidebar.vue';
import PDFPaginator from '@/components/pdf-viewer/PDFPaginator.vue';
import PDFZoom from '@/components/pdf-viewer/PDFZoom.vue';
import KeywordOccurrenceBar from '@/components/pdf-viewer/KeywordOccurrenceBar.vue';
import TextAnnotatorMixin from '@/mixins/text-annotator-mixin';
import DebounceMixin from '@/mixins/debounce-mixin';
import EvaluationLinkMenu from '@/components/pdf-viewer/EvaluationLinkMenu.vue';
import TaskDialog from '@/components/page-opportunity-tasks/TaskDialog.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import { BASE_URL, opportunityDocumentsService } from '@/services/opportunity-documents.service';

import { bus } from '@/main';
import { isPdfFile } from '@/util/document-file-util';
import { buildExtractionSearchQueries } from '@/util/extraction-search-query-builder';
import { paths } from '@/util/paths';
import { mapActions, mapGetters } from 'vuex';
import {
  getTextLinkModalOffset,
  highlightSelectionByUuid,
  removeAllSelectionHighlights,
  removeTemporaryHighlight,
} from '@/util/pdf-annotator-util';
import { isAdminTenant } from '@/models/role';
import axios from 'axios';
import {
  ANNOTATION_COLOR_PRESETS,
  DEFAULT_ANNOTATION_COLOR,
  LAST_ANNOTATION_COLOR_KEY,
} from '@/util/annotation-color-util';
import TaskLinkMenu from '@/components/pdf-viewer/TaskLinkMenu.vue';
import { tenantHasLLMExtractionsAccess, tenantHasTasksAccess } from '@/util/module-access-util';
import { closeOnBack } from '@/util/back-button';
import { viewport } from '@/util/viewport';
import DocumentPermissions from '@/components/pdf-viewer/DocumentPermissions.vue';
import { canAnnotateDocument } from '@/util/document-permissions-util';

const PDF_VIEWER_EXPAND_TIMEOUT = 400; // PDFViewer EXPAND_DIVS_TIMEOUT = 300
const CancelToken = axios.CancelToken;
// Screens that show the document or one of its panels at a time, not side by side.
const SINGLE_PANEL_SIZES = ['phone', 'tablet'];
// What moves the document to show something, which a panel then hides on those screens.
const DOCUMENT_NAVIGATION_EVENTS = [
  'scroll-to-page',
  'render-annotation-page',
  'scroll-to-keyword-page',
  'search-extraction-source',
];
// A keyword picked, or its next or previous occurrence: the document may already show it, and
// scroll there without any of the events above.
const KEYWORD_NAVIGATION_MUTATIONS = [
  'opportunityKeywordSearch/SET_SELECTED_KEYWORD',
  'opportunityKeywordSearch/INCREMENT_KEYWORD_INDEX',
  'opportunityKeywordSearch/DECREMENT_KEYWORD_INDEX',
];
const NAVIGATOR_TABS = { grid: 0, keywords: 1, tasks: 2 };

export default {
  name: 'PageOpportunityAnalysis',
  components: {
    BaseBottomTabs,
    BaseIcon,
    KeywordOccurrenceBar,
    DocumentPermissions,
    TaskLinkMenu,
    BaseLoadingBar,
    EvaluationLinkMenu,
    TaskDialog,
    BaseRoundButton,
    ContextualMenu,
    ColorAnnotationMenu,
    PDFDocument,
    BaseCircularLoadingBar,
    OpportunityHeader,
    PDFNavigator,
    PDFThumbnailSidebar,
    PDFPaginator,
    PDFZoom,
    BaseSelect,
  },
  mixins: [TextAnnotatorMixin, ContextualMenuMixin, DebounceMixin],
  props: {
    opportunityId: String,
    documentId: String,
    page: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  data() {
    return {
      showThumbnails: !SINGLE_PANEL_SIZES.includes(viewport.size),
      // Phones and tablets: 'document', or the panel over it ('grid', 'keywords', 'tasks').
      panel: 'document',
      navigatorPanel: 'grid',
      // Phones and tablets: the task open is set aside while its citation shows in the document.
      taskAside: false,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      isMenuExceedingViewportWidth: false,
      isMenuExceedingViewportHeight: false,
      topPosition: 0,
      interval: null,
      maximumDocuments: 4,
      annotationToHighlight: null,
      baseNode: null,
      cancelToken: null,
      documentDownloading: false,
      extractionSearchLoading: false,
      extractionSearchTriggered: false,
      selectedTask: null,
      isTaskDialogFullScreen: false,
      latestAnnotationColor: null,
      annotationColorUpdateTargets: {},
      colorMenuPosition: {
        top: 42,
        left: 32,
      },
      pollSubscriber: null,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    isSinglePanel() {
      return SINGLE_PANEL_SIZES.includes(viewport.size);
    },
    navigatorTab() {
      return NAVIGATOR_TABS[this.navigatorPanel];
    },
    panelTabs() {
      const tasksLocked = !tenantHasTasksAccess();
      return [
        { key: 'document', label: this.$t('page.analysis.panels.document'), icon: 'file-text' },
        { key: 'grid', label: this.$t('page.analysis.panels.grid'), icon: 'grid' },
        { key: 'keywords', label: this.$t('page.analysis.panels.keywords'), icon: 'key' },
        {
          key: 'tasks',
          label: this.$t('page.analysis.panels.tasks'),
          icon: tasksLocked ? 'lock' : 'clipboard',
          disabled: tasksLocked,
        },
      ];
    },
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('opportunity', ['opportunity']),
    ...mapGetters('opportunity', { opportunityPending: 'pending' }),
    ...mapGetters('opportunityDocuments', ['opportunityDocument', 'opportunityDocuments']),
    ...mapGetters('opportunityDocuments', { opportunityDocumentsStatus: 'status' }),
    ...mapGetters('annotations', ['annotations', 'selectedAnnotation', 'linkedAnnotations']),
    ...mapGetters('annotations', { annotationsPending: 'pending' }),
    ...mapGetters('pdfData', ['pdf', 'pendingRendering', 'loadingTask']),
    ...mapGetters('currentDocument', ['currentPageNumber']),
    ...mapGetters('documentKeywords', { documentKeywords: 'keywords' }),
    ...mapGetters('pdfData', ['pdfScale']),
    ...mapGetters('tasks', ['tasks']),
    ...mapGetters('evaluationGrid', ['evaluationFields']),
    annotationAllowed() {
      return canAnnotateDocument(this.opportunityDocument);
    },
    opportunityDocumentsPending() {
      if (!this.opportunity) {
        return false;
      }
      const oppStatus = this.opportunityDocumentsStatus[this.opportunity.id];
      return oppStatus ? oppStatus.pending : false;
    },
    isAdmin() {
      return isAdminTenant(this.currentUser.role);
    },
    getHorizontalPosition() {
      return this.isMenuExceedingViewportWidth ? { left: '-274px' } : { left: '44px' };
    },
    getVerticalPosition() {
      return this.isMenuExceedingViewportHeight ? { top: this.topPosition + 'px' } : { top: '0' };
    },
    pdfDocuments() {
      if (!this.opportunityDocuments) return [];
      return this.opportunityDocuments.filter((doc) => isPdfFile(doc));
    },
    annotationColorPresets() {
      return ANNOTATION_COLOR_PRESETS;
    },
    activeAnnotationColor() {
      if (this.annotationToHighlight && this.annotationToHighlight.colorCode) {
        return this.annotationToHighlight.colorCode;
      }
      if (this.selectedAnnotation && this.selectedAnnotation.colorCode) return this.selectedAnnotation.colorCode;
      return this.latestAnnotationColor || DEFAULT_ANNOTATION_COLOR;
    },
  },
  methods: {
    findFieldValueById(id) {
      if (!this.evaluationFields) return null;
      for (const field of this.evaluationFields) {
        const value = field.values && field.values.find((v) => v.id === id);
        if (value) return value;
      }
      return null;
    },
    tryExtractionSearch() {
      const fieldValueId = this.$route.query.fieldValueId;
      if (!fieldValueId || this.extractionSearchTriggered || this.pendingRendering) return;

      const fieldValue = this.findFieldValueById(fieldValueId);
      if (!fieldValue) {
        // If evaluation fields are loaded but the field value was not found (deleted, invalid ID),
        // stop showing the loading overlay.
        if (this.evaluationFields) {
          this.extractionSearchLoading = false;
        }
        return;
      }

      this.extractionSearchTriggered = true;
      const queries = buildExtractionSearchQueries(fieldValue.value);
      if (queries) {
        bus.$once('extraction-search-done', () => {
          this.extractionSearchLoading = false;
        });
        bus.$emit('search-extraction-source', {
          queries,
          targetPage: parseInt(this.$route.query.page) || null,
        });
      } else {
        this.extractionSearchLoading = false;
      }
    },
    ...mapActions('opportunity', ['getOpportunity']),
    ...mapActions('opportunityDocuments', ['getDocument', 'setCurrentDocument']),
    ...mapActions('opportunityDocuments', { resetOpportunityDocuments: 'RESET' }),
    ...mapActions('pdfData', ['setPdfUrl']),
    ...mapActions('pdfData', { resetPdf: 'RESET' }),
    ...mapActions('currentDocument', ['setCurrentPageNumber']),
    ...mapActions('currentDocument', { resetCurrentDocument: 'RESET' }),
    ...mapActions('annotations', { resetAnnotations: 'RESET' }),
    ...mapActions('opportunityDocumentsPdfEngine', ['getOpportunityMatchInformation']),
    ...mapActions('documentKeywords', ['getKeywords']),
    ...mapActions('annotations', [
      'getOpportunityDocumentsAnnotations',
      'resetSelectedAnnotation',
      'addDocumentAnnotation',
      'updateDocumentAnnotation',
      'setSelectedAnnotation',
    ]),
    ...mapActions('evaluationGrid', ['getEvaluationGrid', 'addEvalGridValue', 'updateEvalGridValue']),
    ...mapActions('taskCitations', ['createTaskCitation']),
    ...mapActions('tasks', ['getOpportunityTasks', 'updateOpportunityTaskAssignees', 'generateOpportunityTasks']),
    ...mapActions('taskStatuses', ['getTaskStatuses']),
    ...mapActions('taskTemplates', ['getTaskTemplates']),
    ...mapActions('opportunityDocumentsQueue', ['pollForQueuedDocuments', 'stopPollForQueuedDocuments']),
    ...mapActions('extractionJob', ['watchExtractionJob']),
    tenantHasLLMExtractionsAccess,
    /**
     * Refetches the keyword matches after the document keywords changed, so that the highlights and the
     * counts on screen reflect the new keywords instead of the ones the page was loaded with.
     *
     * Debounced because editing keywords comes in bursts: recomputing the matches costs one search per
     * keyword of the tenant, and the backend discards the result of a computation that was already running
     * when the keywords changed, so firing on every single edit would throw most of that work away.
     */
    onDocumentKeywordsChanged() {
      this.debounce(() => this.getOpportunityMatchInformation({ opportunityId: this.opportunityId }), 1500);
    },
    pageRendered(pageNumber) {
      if (!this.opportunityDocument) return;

      // Render annotation page by page
      if (this.annotations && Array.isArray(this.annotations)) {
        const pageSelection = this.annotations.filter(
          (annotation) =>
            pageNumber >= annotation.startPage &&
            pageNumber <= annotation.endPage &&
            annotation.opportunityDocumentId === this.opportunityDocument.id,
        );

        if (pageSelection.length) {
          this.renderAnnotations(pageSelection);
        }
      }
    },
    renderAnnotations(annotations) {
      let filteredAnnotations = [];
      if (this.isAdmin) filteredAnnotations = annotations;
      else {
        const keyAnnotations = this.linkedAnnotations
          .filter((annotation) => annotation.values.some((value) => value.valueType === 'KEY'))
          .flatMap((a) => a.textAnnotationId);
        filteredAnnotations = annotations.filter((annotation) => !keyAnnotations.includes(annotation.id));
      }

      // Strip keyword highlights that split text nodes before resolving annotation XPaths
      const pages = [...new Set(filteredAnnotations.map((a) => a.startPage))];
      const pagesToRestore = pages.filter((page) => this.stripKeywordElements(page));

      filteredAnnotations.forEach((annotation) => {
        let range = this.getRangeFromSelection(annotation);
        if (!range) return;

        const isLinked = this.linkedAnnotations.some((annot) => annot.textAnnotationId === annotation.id);

        this.highlightRange(range, annotation.id, isLinked, false, annotation.colorCode);
      });

      // Re-render keyword highlights (synchronous, no visual flash)
      if (pagesToRestore.length > 0) {
        bus.$emit('re-render-keywords', pagesToRestore);
      }
    },
    updateViewportDimensions() {
      this.viewportWidth = window.innerWidth;
      this.viewportHeight = window.innerHeight;
    },
    checkViewportExceed(size) {
      const previousTopPosition = this.topPosition;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      this.isMenuExceedingViewportWidth =
        size.width + this.menuContainer.left + getTextLinkModalOffset() > viewportWidth;
      this.isMenuExceedingViewportHeight = size.height + this.menuContainer.top + 125 > viewportHeight;
      if (this.menuContainer.top + this.topPosition >= 0) {
        this.topPosition = viewportHeight - (size.height + this.menuContainer.top + 275);
      }
      if (this.menuContainer.top + this.topPosition <= 0) {
        this.topPosition = previousTopPosition;
      }
    },
    updateCurrentDocumentFromBaseSelect(selectedDocument) {
      const document = this.opportunityDocuments.find((doc) => doc.id === selectedDocument.documentId);
      this.updateCurrentDocument(document);
    },
    updateCurrentDocument(document) {
      this.resetCurrentDocument();

      // Handle the case when the document is a string (id) instead of an object
      if (document && typeof document !== 'object') {
        document = this.pdfDocuments.find((doc) => doc.id === document);
      }

      if (!document || !this.opportunityDocument || document.id === this.opportunityDocument.id) return;

      // Check if the document is not a PDF file
      if (!isPdfFile(document)) {
        this.$toast.warning(this.$t('page.opportunity.access.unavailable-document-visualization'));
        this.$router.push(paths.opportunities);
        return;
      }

      this.setCurrentDocument({ document });

      this.$router.push({
        name: 'opportunity-analysis',
        params: { opportunityId: document.opportunityId, documentId: document.id },
      });
    },
    downloadDocument(doc) {
      this.documentDownloading = true;
      opportunityDocumentsService
        .getDocumentFile({ opportunityId: doc.opportunityId, documentId: doc.id })
        .then((fileBlob) => {
          const fileURL = window.URL.createObjectURL(fileBlob);
          const link = document.createElement('a');
          link.href = fileURL;
          link.download = doc.name;
          link.click();
          link.remove();
          window.URL.revokeObjectURL(fileURL);
        })
        .finally(() => {
          this.documentDownloading = false;
        });
    },
    scroll(page) {
      bus.$emit('scroll-to-page', parseInt(page));
    },
    onmouseup(e) {
      if (!window.getSelection) return;
      let sel = window.getSelection();
      this.baseNode = sel.baseNode || sel.anchorNode;

      // If annotation is not collapsed, it means we are creating a new annotation
      if (!sel.isCollapsed) {
        // Check if selected text is not empty or only spaces
        if (sel.toString().trim().length === 0) {
          this.clearSelectionRanges();
          return;
        }

        let range = sel.getRangeAt(0);
        this.annotationToHighlight = this.createSelectionFromRange(range);
        this.annotationToHighlight.colorCode = this.latestAnnotationColor || DEFAULT_ANNOTATION_COLOR;
        this.textSelection = true;
        this.markCurrentAnnotation({ annotation: this.annotationToHighlight, temporary: true });
        this.clearSelectionRanges();

        // Show contextual menu
        this.showContextualMenus(e);
        // The tag submenu is the default action on selection; skip it when the PDF forbids annotations.
        if (this.annotationAllowed) this.showTagModal();
      } else {
        // Selection is collapsed, check if we're selecting an existing selection
        if (!this.baseNode) return;

        const selectionId = this.getSelectionUuidFromCoordinates(this.baseNode, e.pageX, e.pageY);

        if (!selectionId && this.annotationToHighlight) {
          this.removeHighlightRange(this.annotationToHighlight);
        } else if (selectionId) {
          if (selectionId !== 'temporary') {
            this.textSelection = false;
            this.selectExistingSelectionFromUuid(selectionId);
          }
          this.showContextualMenus(e);
        }
      }
    },
    findExistingAnnotation(annotation) {
      if (!annotation || !annotation.text) return null;
      return this.annotations.find((a) => a.text === annotation.text) || null;
    },
    highlightAnnotation(linkMenu) {
      const annotation = this.annotationToHighlight ? this.annotationToHighlight : this.selectedAnnotation;
      if (this.annotationToHighlight) {
        this.removeHighlightRange(this.annotationToHighlight);
      }
      if (!annotation) return;

      // avoid creating another annotation with same content
      const existingAnnotation = this.findExistingAnnotation(annotation);
      if (existingAnnotation) {
        this.setSelectedAnnotation(existingAnnotation);
        if (!linkMenu) {
          this.$toast.success(this.$t('toast.success'));
          this.textSelection = false;
        }
        this.closeMenu();
        this.clearSelectionRanges();
        this.annotationToHighlight = null;
        return;
      }

      // Add the document annotation
      this.addDocumentAnnotation({
        opportunityId: this.opportunityId,
        docId: this.documentId,
        selection: annotation,
      })
        .then((createdAnnotation) => {
          this.saveLatestAnnotationColor(createdAnnotation.colorCode);
          this.setSelectedAnnotation(createdAnnotation);
          if (!linkMenu) {
            this.$toast.success(this.$t('toast.success'));
            this.textSelection = false;
            this.closeMenu();
          }
        })
        .catch((error) => {
          this.resetSelectedAnnotation();
          this.$toast.error(error);
        })
        .finally(() => {
          const selectedAnnotation = this.selectedAnnotation;
          // We need to wait for a certain delay for pdf viewer to remove its padding from textLayer
          setTimeout(() => {
            // Mark the current annotation
            this.markCurrentAnnotation({ annotation: selectedAnnotation, temporary: false });
            // Clear current browser selection range
            this.clearSelectionRanges();
          }, PDF_VIEWER_EXPAND_TIMEOUT);
        });

      this.annotationToHighlight = null;
    },
    closeMenu() {
      this.hideContextualMenus();
    },
    onmousedown(e) {
      if (this.isTemporaryAnnotation()) {
        const selectionId = this.getSelectionUuidFromCoordinates(this.baseNode, e.pageX, e.pageY);
        if (!selectionId) removeTemporaryHighlight();
      }
      this.resetSelectedAnnotation();
      this.hideContextualMenus();
      removeAllSelectionHighlights();
    },
    markCurrentAnnotation({ annotation, temporary }) {
      // Check if it is an empty selection
      let range = this.getRangeFromSelection(annotation);
      if (!range || annotation.text.trim().length === 0) return;
      const id = annotation.id ? annotation.id : 'temporary';
      const annotationColorCode = temporary ? null : annotation.colorCode;
      this.highlightRange(range, id, false, temporary, annotationColorCode);
    },
    selectExistingSelectionFromUuid(id) {
      highlightSelectionByUuid(id);
      this.setSelectedAnnotation(this.annotations.find((sel) => sel.id === id));
    },
    linkSelectionToItem(item) {
      this.addEvalGridValue({
        opportunityId: this.opportunityId,
        fieldId: item.id,
        source: 'PDF_ANNOTATION',
        textAnnotationId: this.selectedAnnotation.id,
        opportunityDocumentId: this.opportunityDocument.id,
        value: this.selectedAnnotation.text,
        valueType: item.valueType,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.closeMenu();
        })
        .catch((error) => {
          this.resetSelectedAnnotation();
          this.$toast.error(error);
        });
    },
    linkSelectionToSelectItem(item) {
      this.addEvalGridValue({
        opportunityId: this.opportunityId,
        fieldId: item.id,
        source: 'PDF_ANNOTATION',
        textAnnotationId: this.selectedAnnotation.id,
        opportunityDocumentId: this.opportunityDocument.id,
        value: item.value,
        valueType: item.valueType,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.closeMenu();
        })
        .catch((error) => {
          this.resetSelectedAnnotation();
          this.$toast.error(error);
        });
    },
    updateLinkSelectionToItem(item) {
      let selection = this.selectedAnnotation;

      this.updateEvalGridValue({
        opportunityId: this.opportunityId,
        fieldId: item.evaluationGridFieldId,
        id: item.id,
        value: selection.text,
        valueType: item.valueType,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
          this.closeMenu();
        })
        .catch((error) => {
          this.resetSelectedAnnotation();
          this.$toast.error(error);
        });
    },
    onTaskCreateMenuOptionClick() {
      const annotation = this.annotationToHighlight ? this.annotationToHighlight : this.selectedAnnotation;
      if (this.annotationToHighlight) {
        this.removeHighlightRange(this.annotationToHighlight);
      }

      const openTaskDialog = (citations = []) => {
        this.selectedTask = {
          id: null,
          opportunityId: this.opportunityId,
          title: '',
          description: '',
          taskStatus: null,
          assignations: [],
          citations,
        };
        this.hideTaskModal();
        this.closeMenu();
      };

      if (!annotation) {
        openTaskDialog();
        return;
      }

      // avoid creating another annotation with same content
      const existingAnnotation = this.findExistingAnnotation(annotation);
      if (existingAnnotation) {
        this.setSelectedAnnotation(existingAnnotation);
        const citation = {
          source: 'PDF_ANNOTATION',
          textAnnotationId: existingAnnotation.id,
          opportunityDocumentId: this.opportunityDocument.id,
          opportunityDocumentPage: null,
          value: existingAnnotation.text,
        };
        openTaskDialog([citation]);
        this.clearSelectionRanges();
        this.annotationToHighlight = null;
        return;
      }

      this.addDocumentAnnotation({
        opportunityId: this.opportunityId,
        docId: this.documentId,
        selection: annotation,
      })
        .then((createdAnnotation) => {
          this.saveLatestAnnotationColor(createdAnnotation.colorCode);
          this.setSelectedAnnotation(createdAnnotation);
          const citation = {
            source: 'PDF_ANNOTATION',
            textAnnotationId: createdAnnotation.id,
            opportunityDocumentId: this.opportunityDocument.id,
            opportunityDocumentPage: null,
            value: createdAnnotation.text,
          };
          openTaskDialog([citation]);
        })
        .catch((error) => {
          this.$toast.error(error);
          openTaskDialog();
        })
        .finally(() => {
          const selectedAnnotation = this.selectedAnnotation;
          setTimeout(() => {
            this.markCurrentAnnotation({ annotation: selectedAnnotation, temporary: false });
            this.clearSelectionRanges();
          }, PDF_VIEWER_EXPAND_TIMEOUT);
        });

      this.annotationToHighlight = null;
    },
    onTaskDialogClose() {
      this.selectedTask = null;
      this.taskAside = false;
      this.isTaskDialogFullScreen = false;
    },
    saveLatestAnnotationColor(colorCode) {
      if (!colorCode) return;
      this.latestAnnotationColor = colorCode;
      localStorage.setItem(LAST_ANNOTATION_COLOR_KEY, colorCode);
    },
    setColorMenuPosition(position) {
      if (!position) return;
      this.colorMenuPosition = {
        top: position.top || 42,
        left: position.left || 32,
      };
    },
    applyColorToAnnotationElements(annotationId, colorCode) {
      const colorRgb = this.hexToRgb(colorCode);
      const annotationElements = document.querySelectorAll(`.annotation-${annotationId}`);
      const annotationAnchorElements = document.querySelectorAll(`.annotation-anchor-${annotationId}`);

      annotationElements.forEach((element) => {
        if (!colorRgb) {
          element.style.removeProperty('--annotation-bg');
          element.style.removeProperty('--annotation-bg-selected');
          element.style.removeProperty('--annotation-bg-temporary');
          return;
        }
        element.style.setProperty('--annotation-bg', `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.4)`);
        element.style.setProperty('--annotation-bg-selected', `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.6)`);
        element.style.setProperty(
          '--annotation-bg-temporary',
          `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.3)`,
        );
      });

      annotationAnchorElements.forEach((element) => {
        if (!colorRgb) {
          element.style.removeProperty('--annotation-anchor-bg');
          element.style.removeProperty('--annotation-anchor-bg-selected');
          return;
        }
        element.style.setProperty('--annotation-anchor-bg', `rgb(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b})`);
        element.style.setProperty(
          '--annotation-anchor-bg-selected',
          `rgba(${colorRgb.r}, ${colorRgb.g}, ${colorRgb.b}, 0.85)`,
        );
      });
    },
    onSelectAnnotationColor(colorCode) {
      this.saveLatestAnnotationColor(colorCode);
      if (this.annotationToHighlight) {
        this.removeHighlightRange(this.annotationToHighlight);
        this.annotationToHighlight = { ...this.annotationToHighlight, colorCode };
        this.markCurrentAnnotation({ annotation: this.annotationToHighlight, temporary: true });
        return;
      }
      if (!this.selectedAnnotation) return;
      const annotationId = this.selectedAnnotation.id;
      const pendingColorCode = this.annotationColorUpdateTargets[annotationId];
      if (pendingColorCode === colorCode) return;
      if (this.selectedAnnotation.colorCode === colorCode) return;

      const previousAnnotation = { ...this.selectedAnnotation };
      this.$set(this.annotationColorUpdateTargets, annotationId, colorCode);
      this.setSelectedAnnotation({ ...previousAnnotation, colorCode });
      this.applyColorToAnnotationElements(previousAnnotation.id, colorCode);

      this.updateDocumentAnnotation({
        opportunityId: this.opportunityId,
        docId: this.selectedAnnotation.opportunityDocumentId || this.documentId,
        id: this.selectedAnnotation.id,
        payload: { colorCode },
      })
        .then((updatedAnnotation) => {
          this.setSelectedAnnotation({ ...previousAnnotation, ...updatedAnnotation, colorCode });
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => {
          this.applyColorToAnnotationElements(previousAnnotation.id, previousAnnotation.colorCode);
          this.setSelectedAnnotation(previousAnnotation);
          this.$toast.error(error);
        })
        .finally(() => {
          if (this.annotationColorUpdateTargets[annotationId] === colorCode) {
            this.$delete(this.annotationColorUpdateTargets, annotationId);
          }
        });
    },
    // A panel tapped something the document shows: the document comes back over it.
    showDocument() {
      if (this.isSinglePanel) this.panel = 'document';
    },
    onScrollToPage(page) {
      if (this.isSinglePanel) this.taskAside = true;
      this.$nextTick(() => {
        this.scroll(page);
        this.setCurrentPageNumber(parseInt(page));
      });
    },
    onTaskEdit(updatedTask) {
      this.selectedTask = updatedTask;
      this.taskAside = false;
    },
    onTaskCitationDelete(citation) {
      this.selectedTask.citations = this.selectedTask.citations.filter((c) => c.id !== citation.id);
    },
    onTaskCreateClick() {
      this.taskAside = false;
      this.selectedTask = {
        id: null,
        opportunityId: this.opportunityId,
        title: '',
        description: '',
        taskStatus: null,
        assignations: [],
        citations: [],
      };
    },
    onTaskAssign({ task, userIds }) {
      this.updateOpportunityTaskAssignees({
        opportunityId: task.opportunityId,
        taskId: task.id,
        assigneeIds: userIds,
      }).then((updatedTask) => {
        if (updatedTask.id === this.selectedTask.id) {
          this.selectedTask = updatedTask;
        }
      });
    },
    onTaskDialogTransitionEnd() {
      const isCreateTaskMode = this.selectedTask && !this.selectedTask.id;
      if (this.$refs.taskDialog && isCreateTaskMode) {
        this.$refs.taskDialog.focusOnTitleInput();
      }
    },
    onImportTasks() {
      this.generateOpportunityTasks({ opportunityId: this.opportunityId }).then(() => {
        const totalTasksFromTemplate = this.tasks.filter((t) => !!t.taskTemplateId).length;
        this.$toast.success(this.$t('opportunity-tasks.import-tasks-success', { total: totalTasksFromTemplate }));
      });
    },
    onLinkSelectionToTask(task) {
      if (!this.selectedAnnotation) return;
      this.createTaskCitation({
        opportunityId: this.opportunityId,
        taskId: task.id,
        citation: {
          source: 'PDF_ANNOTATION',
          textAnnotationId: this.selectedAnnotation.id,
          opportunityDocumentId: this.opportunityDocument.id,
          opportunityDocumentPage: null,
          value: this.selectedAnnotation.text,
        },
      })
        .then((createdCitation) => {
          this.$toast.success(this.$t('toast.success'));
          if (this.selectedTask && this.selectedTask.id === task.id) {
            this.selectedTask = {
              ...this.selectedTask,
              citations: [...this.selectedTask.citations, createdCitation],
            };
          }
        })
        .catch(() => {
          this.$toast.error(this.$t('toast.error'));
        });
    },
    handleError(error) {
      if (!error.response) this.$router.replace(paths.notFound);

      // If a not found or forbidden error occurs, redirect to not-found page
      if (error.response.status === 404 || error.response.status === 403) {
        this.$router.replace(paths.notFound);
      } else {
        // Otherwise show an error toast
        console.error(error);
        this.$toast.error(error);
        throw error;
      }
    },
  },
  async created() {
    try {
      this.latestAnnotationColor = localStorage.getItem(LAST_ANNOTATION_COLOR_KEY);
      if (this.$route.query.fieldValueId) {
        this.extractionSearchLoading = true;
      }
      if (this.cancelToken) this.cancelToken.cancel('Request aborted by user.');
      this.cancelToken = CancelToken.source();

      const opportunityId = this.opportunityId;
      const documentId = this.documentId;
      const cancelToken = this.cancelToken;

      const pdfUrl = `${BASE_URL}/${opportunityId}/documents/${documentId}/file`;
      this.setPdfUrl({ pdfUrl });

      const [, document] = await Promise.all([
        this.getOpportunity({ id: opportunityId, cancelToken }),
        this.getDocument({ opportunityId, documentId, cancelToken }),
        this.getKeywords(),
      ]);

      if (!document) return;

      if (this.tenantHasLLMExtractionsAccess()) {
        this.watchExtractionJob({ opportunityId: this.opportunityId });
      }
      this.pollSubscriber = this.pollForQueuedDocuments({ opportunityId: this.opportunityId });
      this.getOpportunityDocumentsAnnotations({
        opportunityId,
        cancelToken,
      });
      this.getEvaluationGrid(this.opportunityId);
      this.getOpportunityTasks({ opportunityId });
      this.getTaskStatuses();
      this.getTaskTemplates();

      if (this.documentKeywords.length > 0) {
        this.getOpportunityMatchInformation({ opportunityId });
      }
    } catch (error) {
      this.handleError(error);
    }
  },
  mounted() {
    bus.$on('change-current-document', (document) => this.updateCurrentDocument(document));
    bus.$on('document-keywords-changed', this.onDocumentKeywordsChanged);
    DOCUMENT_NAVIGATION_EVENTS.forEach((event) => bus.$on(event, this.showDocument));
    this.stopWatchingKeywords = this.$store.subscribe(({ type }) => {
      if (KEYWORD_NAVIGATION_MUTATIONS.includes(type)) this.showDocument();
    });
    window.addEventListener('resize', this.updateViewportDimensions);
  },
  watch: {
    panel(to, from) {
      if (to !== 'document') this.navigatorPanel = to;
      // On phones, back closes the panel, back to the document (util/back-button).
      if (from === 'document') {
        this.releasePanelBack = closeOnBack(() => {
          this.panel = 'document';
        });
      } else if (to === 'document' && this.releasePanelBack) {
        this.releasePanelBack();
        this.releasePanelBack = null;
      }
    },
    // Handle document navigation using $route within PageOpportunityAnalysis
    $route(to, from) {
      if (to.name !== 'opportunity-analysis' || from.name !== 'opportunity-analysis') return;

      const newDocId = to.params.documentId;
      const oldDocId = from.params.documentId;

      if (newDocId !== oldDocId) {
        // Skip if the document switch was already initiated programmatically
        // (e.g. by updateCurrentDocument which calls setCurrentDocument before pushing the route)
        if (this.opportunityDocument && this.opportunityDocument.id === newDocId) return;

        const document = this.pdfDocuments.find((doc) => doc.id === newDocId);
        if (document && isPdfFile(document)) {
          this.resetCurrentDocument();
          this.setCurrentDocument({ document });
        }
      } else if (to.query.page) {
        this.$nextTick(() => {
          this.scroll(to.query.page);
          this.setCurrentPageNumber(parseInt(to.query.page));
        });
      }
    },
    // When annotations finish loading after the PDF is already rendered,
    // apply them to all pages that have already been rendered.
    annotationsPending(pending) {
      if (!pending && this.pdf && !this.pendingRendering && this.annotations) {
        const numPages = this.pdf.numPages;
        for (let page = 1; page <= numPages; page++) {
          const pageSelection = this.annotations.filter(
            (annotation) =>
              page >= annotation.startPage &&
              page <= annotation.endPage &&
              annotation.opportunityDocumentId === this.opportunityDocument.id,
          );
          if (pageSelection.length) {
            this.renderAnnotations(pageSelection);
          }
        }
      }
    },
    pendingRendering() {
      const pageInUrl = this.$route.query.page;
      if (!this.pendingRendering && pageInUrl) {
        this.$nextTick(() => {
          this.scroll(pageInUrl);
          this.setCurrentPageNumber(pageInUrl);
          this.tryExtractionSearch();
        });
      }
    },
    evaluationFields() {
      this.tryExtractionSearch();
    },
  },
  beforeDestroy() {
    this.stopPollForQueuedDocuments({ subscriber: this.pollSubscriber });
    bus.$off('change-current-document');
    // Passes the handler so that the listener of another view on the same event is left in place
    bus.$off('document-keywords-changed', this.onDocumentKeywordsChanged);
    DOCUMENT_NAVIGATION_EVENTS.forEach((event) => bus.$off(event, this.showDocument));
    this.stopWatchingKeywords();
    if (this.releasePanelBack) this.releasePanelBack();
    // Drops a refetch still pending from a last-moment keyword change
    this.debounce(null, 0, true);
    window.removeEventListener('resize', this.updateViewportDimensions);

    if (this.cancelToken) this.cancelToken.cancel('Request aborted by user.');

    // Stops the file from loading
    if (this.loadingTask) {
      // Note: Destroying the loadingTask causes an error to be thrown in the console:
      // Uncaught (in promise) Error: Worker was terminated
      // Ideally we would like to avoid or catch this error but it doesn't seem trivial
      if (this.loadingTask._worker && !this.loadingTask._worker.destroyed) {
        this.loadingTask._worker.destroy();
      }
      if (!this.loadingTask.destroyed) {
        this.loadingTask.destroy();
      }
    }
  },
  destroyed() {
    this.resetCurrentDocument();
    this.resetOpportunityDocuments();
    this.resetPdf();

    this.resetAnnotations();

    clearInterval(this.interval);
  },
};
</script>

<style lang="scss">
.opportunity-analysis {
  height: calc(var(--viewport-height) - 56px);
  width: 100%;
  position: relative;
  max-width: 100vw;

  &__loading {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 16px 32px 0 32px;
    font-size: 12px;
  }

  &__content {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: calc(100% - 84px);
    overflow: hidden;
    position: absolute;

    // Below laptop size the header has two rows, and the bottom tabs (BaseBottomTabs) take their
    // place under the document or the panel over it.
    @include screen-below('laptop') {
      height: calc(100% - #{$opportunity-header-compact-height + 12px});
      padding-bottom: calc(#{$bottom-tabs-height} + env(safe-area-inset-bottom));
    }

    &__contextmenu {
      &-container {
        position: absolute;
        z-index: 3;
      }
    }
  }

  &__panel {
    &--pdfNavigator {
      display: flex;
      flex: 0 1 auto;
      margin-left: auto;
      min-width: 480px;
      max-width: 480px;
      background: $color-white;
      z-index: 2;
      user-select: none;

      // Phones and tablets: a panel over the document, from the top to the bottom tabs.
      &.is-over-document {
        position: absolute;
        top: 0;
        right: 0;
        bottom: calc(#{$bottom-tabs-height} + env(safe-area-inset-bottom));
        left: 0;
        min-width: 0;
        max-width: none;
        z-index: 3;

        .pdf-navigator {
          border-right: 0;
        }
      }
    }
  }

  &__panels {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 3;
  }

  &__occurrences {
    position: absolute;
    top: 8px;
    right: 8px;
    left: 8px;
    z-index: 2;
  }

  &__back-to-task {
    position: absolute;
    bottom: 12px;
    left: 12px;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 16px;
    background: $color-white;
    border-radius: $radius-full;
    box-shadow: $shadow-dropdown;
    color: $color-accent;
    font-weight: 600;

    .base-icon {
      width: 18px;
      height: 18px;
    }
  }

  &__center {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;

    // Phones: the zoom floats over the document (below).
    @include screen-below('tablet') {
      position: relative;
    }
  }

  &__documents-header {
    background: $color-white;
    padding: 8px 12px;
    box-shadow: $shadow-dropdown;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    min-height: 48px;
    border-bottom: 1px solid $color-border;
    border-right: 1px solid $color-border;
    z-index: 1;
    gap: 8px;
    user-select: none;

    // Phones: the document's name, its page and its actions on one line; the zoom over the
    // document, at the bottom right, under the thumb.
    @include screen-below('tablet') {
      z-index: 2;
      @include floating-pdf-zoom;
    }
  }

  &__header-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__header-center {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    flex-shrink: 0;
    padding: 0 16px;

    @include screen-below('tablet') {
      padding: 0;
    }
  }

  &__header-separator {
    width: 1px;
    height: 14px;
    background: $color-border;
    flex-shrink: 0;

    @include screen-below('tablet') {
      display: none;
    }
  }

  &__header-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    flex: 1;

    @include screen-below('tablet') {
      flex: none;
    }
  }

  &__table-content {
    background: $color-white;
    padding: 2rem;
    width: 300px;
  }

  &__pdf-content {
    overflow: hidden;
    height: 100%;
    position: relative;
  }

  &__pdf-content-viewer {
    position: relative;
    height: 100%;
  }

  &__documents--select {
    flex: 1;
    min-width: 0;

    .v-input__control {
      max-width: 480px;
    }

    .v-menu__content {
      max-width: none !important;
    }

    // Override BaseSelect styles for compact header layout
    .base-select.is-small {
      .v-input__slot {
        min-height: 34px !important;
        max-width: 320px !important;
        border-radius: 99px !important;
        padding: 0 8px !important;
      }

      .v-select__selections .v-select__selection {
        font-size: 14px;
      }
    }
  }

  &__task-dialog {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 50%;
    max-width: 1024px;
    z-index: 4;
    overflow: hidden;
    display: flex;
    box-shadow: $shadow-default;
    transition: width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), max-width 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

    // Phones and tablets: the whole screen, over the bottom tabs.
    @include screen-below('laptop') {
      width: 100%;
      max-width: none;
    }

    &--fullscreen {
      width: 100%;
      max-width: 100%;
    }

    .task-dialog {
      flex: 1;
    }
  }
}

.analysis-task-dialog-slide-enter-active,
.analysis-task-dialog-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.analysis-task-dialog-slide-enter,
.analysis-task-dialog-slide-leave-to {
  transform: translateX(100%);
}

.analysis-task-dialog-slide-enter-to,
.analysis-task-dialog-slide-leave {
  transform: translateX(0);
}
</style>
